class MeasuringGuide extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      bodyMeasurementsJSon: '[data-body-measurements-json]',
      currentMeasurement: '[data-current-measurement]',
      selector: '[data-measurements-selector]',
      selectorTrigger: '[data-measurements-selector-trigger]',
      selectorTriggerText: '[data-measurements-selector-trigger-text]',
      selectorRadio: '[data-measurements-selector-radio]',
      accordionInput: 'input[name="measuring-guide-step"]',
      panel: '[data-panel]',
      marker: '[data-marker]',
      markerIndex: '[data-marker-index]',
      tab: '[data-tab-content]',
    };

    this.classes = {
      sizeSelectorActive: 'size-guide__selector--active',
    };

    this.selectorContainer = this.querySelector(this.selectors.selector);
    this.selectorTrigger = this.selectorContainer?.querySelector(this.selectors.selectorTrigger);
    this.selectorTriggerText = this.selectorContainer?.querySelector(this.selectors.selectorTriggerText);
    this.sizeRadios = this.selectorContainer?.querySelectorAll(this.selectors.selectorRadio);
    this.accordionRadios = this.querySelectorAll(this.selectors.accordionInput);
    this.panels = this.querySelectorAll(this.selectors.panel);
    this.marker = this.querySelector(this.selectors.marker);
    this.markerIndex = this.marker?.querySelector(this.selectors.markerIndex);
    this.bodyMeasurementsJsonElem = this.querySelector(this.selectors.bodyMeasurementsJSon);
    this.bodyMeasurements = JSON.parse(
      this.bodyMeasurementsJsonElem ? this.bodyMeasurementsJsonElem.textContent : '{}'
    );
    this.bodyMeasurementsSizes = Object.keys(this.bodyMeasurements);
    this.selectedSize = this.selectorTriggerText?.textContent ?? null;
    this.unit = 'cm';
    this.currentMeasurementElem = this.querySelector(this.selectors.currentMeasurement);
    this.selectedMeasurementType = this.accordionRadios[0].value;
  }

  connectedCallback() {
    this.initSizeSelectorToggle();
    this.initSizeChange();
    this.initAccordions();
    this.initUnitChangeListener();
    this.initVariantChangeListener();
  }

  initAccordions() {
    if (!this.panels.length) return;

    this.openFirstAccordion();

    this.accordionRadios?.forEach((radio, index) => {
      radio.addEventListener('change', () => {
        const measurementType = radio.value;
        const measurementHandle = radio.dataset.measurementHandle;

        // Update class content for marker
        if (this.marker) {
          this.marker.className = `measuring-guide__marker measuring-guide__marker--${measurementHandle}`;

          if (this.markerIndex) this.markerIndex.textContent = index + 1;
        }

        // Close all panels and open the selected one
        this.panels.forEach((panel) => (panel.style.height = 0));

        const selectedPanel = this.panels[index];
        if (selectedPanel) {
          selectedPanel.style.height = `${selectedPanel.scrollHeight}px`;
        }

        // Update measurement displayed
        this.selectedMeasurementType = measurementType;
        this.updateMeasurements();
      });
    });
  }

  openFirstAccordion() {
    const tab = this.closest(this.selectors.tab);
    if (!tab) return;

    const tabIsActive = !tab.hasAttribute('aria-hidden') || tab.getAttribute('aria-hidden') === 'false';

    // Opens first accordion panel immediately if tab is visible, or waits for tab visibility to change
    if (tabIsActive) {
      this.panels[0].style.height = `${this.panels[0].scrollHeight}px`;
    } else {
      const tabObserver = new MutationObserver((mutations) => {
        const visibilityChanged = mutations.some(
          (mutation) =>
            mutation.attributeName === 'aria-hidden' &&
            (!tab.hasAttribute('aria-hidden') || tab.getAttribute('aria-hidden') === 'false')
        );

        if (visibilityChanged) {
          this.panels[0].style.height = `${this.panels[0].scrollHeight}px`;
          tabObserver.disconnect();
        }
      });
      tabObserver.observe(tab, {
        attributes: true,
        attributeFilter: ['aria-hidden'],
      });
    }
  }

  initSizeSelectorToggle() {
    this.selectorTrigger?.addEventListener('click', () => {
      this.selectorContainer.classList.toggle(this.classes.sizeSelectorActive);
    });

    // Close on click outside
    document.addEventListener('click', (event) => {
      if (event.target.closest(this.selectors.selector)) return;
      this.selectorContainer.classList.remove(this.classes.sizeSelectorActive);
    });
  }

  initSizeChange() {
    this.sizeRadios?.forEach((sizeRadio) => {
      sizeRadio.addEventListener('change', () => {
        if (!sizeRadio.checked) return;

        if (this.selectedSize === sizeRadio.value) return;

        this.selectedSize = sizeRadio.value;

        this.updateMeasurements();

        // Update size selector trigger with selected size
        if (this.selectorTriggerText) {
          this.selectorTriggerText.textContent = sizeRadio.value;
        }

        // Close the size selector
        this.selectorContainer?.classList.remove(this.classes.sizeSelectorActive);
      });
    });
  }

  updateMeasurements() {
    if (!this.selectedSize || !this.selectedMeasurementType) return;

    const currentMeasurement = this.bodyMeasurements[this.selectedSize][this.selectedMeasurementType];
    if (!currentMeasurement) {
      this.currentMeasurementElem.textContent = '';
      return;
    }
    this.currentMeasurementElem.textContent = `${this.selectedMeasurementType} ${currentMeasurement[this.unit]}`;

    this.currentMeasurementElem.innerHTML = `
      ${this.selectedMeasurementType}
      <span
        data-measurement-cm="${currentMeasurement.cm}"
        ${currentMeasurement.in ? `data-measurement-in="${currentMeasurement.in}"` : ''}
        data-measurement
      >${currentMeasurement[this.unit]}</span>
    `;
  }

  initUnitChangeListener() {
    this.addEventListener('size-guide:unit-change', (event) => {
      if (!event.detail.unit) return;
      this.unit = event.detail.unit;
      this.updateMeasurements();
    });
  }

  initVariantChangeListener() {
    this.addEventListener('variant:change', (event) => {
      const variant = event.detail?.variant;
      if (!variant) return;

      // Find the option that matches a measurement size
      const size = variant.options?.find((option) => this.bodyMeasurementsSizes.includes(option));
      if (!size || this.selectedSize === size) return;

      this.selectedSize = size;

      this.updateMeasurements();

      // Update size selector trigger with selected size
      if (this.selectorTriggerText) {
        this.selectorTriggerText.textContent = size;
      }
    });
  }
}

export default function initMeasuringGuide() {
  if (!customElements.get('measuring-guide')) {
    customElements.define('measuring-guide', MeasuringGuide);
  }
}
